import { motion } from "framer-motion";
import { History } from "lucide-react";

import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { cn } from "../../../../lib/utils";
import { useClaimMotion } from "./claimMotion";
import { formatCrmTimestamp, splitBilingualSms } from "./claimStatus";

/**
 * The claim's history, as a timeline.
 *
 * Each CRM entry is one SMS holding the English and Arabic copies of the same
 * sentence back to back. The old page printed both, one under the other, so an
 * English-speaking customer read every update twice — once in a script they
 * could not read. Here the message is split and only the customer's language is
 * shown; if CRM sent just one language, that one is used regardless.
 *
 * The rail is a single border on the list rather than a 2px div per row, which
 * is what left the old version with gaps between entries and no RTL handling.
 */
// Vertical centre of an entry's dot, from the top of that entry's box. A plain
// row starts with its 16px timestamp line, so its dot centres at 8 — pulled to
// 10 to clear the ring. The highlighted card adds 14px of padding above its own
// 16px label line, so that dot centres at 22.
const DOT_CENTRE = 10;
const LATEST_DOT_CENTRE = 22;

const ClaimTimeline = ({ entries }) => {
  const translate = useLanguageTranslate();
  const motionPresets = useClaimMotion();
  const items = Array.isArray(entries) ? entries : [];

  if (!items.length) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-dashed border-[#DDE2E4] bg-[#FBFCFD] px-4 py-5 text-sm text-[#6E7C87]">
        <History className="size-4 shrink-0" aria-hidden="true" />
        {/* Forward-looking rather than "nothing here": this is the main column
            and it is empty most often right after a claim is filed, when the
            customer most wants to know something is happening. */}
        {translate(
          "Updates will appear here as your claim progresses",
          "ستظهر التحديثات هنا مع تقدّم مطالبتك"
        )}
      </div>
    );
  }

  return (
    /* The rail is drawn per entry rather than as one `border-s` on the list.
       A border spans the whole box, so it ran from the top of the list down
       past the first dot — a stub of line hanging above the newest entry's
       circle, which got more obvious once that circle moved down to sit level
       with the card's label. It trailed past the last dot for the same reason.

       Each entry now draws only the segment below its own dot, so the line
       starts and stops exactly on the first and last circles. */
    <motion.ol {...motionPresets.listContainer} className="relative ms-2">
      {items.map((entry, index) => {
        const { en, ar } = splitBilingualSms(entry?.SMS_Content);
        const message = translate(en || ar, ar || en);
        // Treated as the newest because it is the order the API returns and the
        // order this list already renders in. `createdon` is a CRM display
        // string rather than a parseable timestamp, so sorting here would mean
        // guessing between dd/MM and MM/dd and risking a silently reordered
        // history — a worse failure than trusting the given order.
        const isLatest = index === 0;
        // Where this entry's dot sits, measured from the top of its own box.
        // Both land on the middle of the entry's first line of text: the
        // highlighted card adds its 14px of padding before the label.
        const dotCentre = isLatest ? LATEST_DOT_CENTRE : DOT_CENTRE;

        return (
          <motion.li
            key={`${entry?.createdon || "entry"}-${index}`}
            {...motionPresets.listItem}
            className="relative ps-6 pb-7 last:pb-0"
          >
            {/* The segment from this dot down to the next one. Rendered before
                the dot so the dot and its white ring paint over the top of it,
                and carried 10px past this item's box to land exactly on the
                next dot's centre. */}
            {index < items.length - 1 && (
              <span
                aria-hidden="true"
                className="absolute start-0 w-px -translate-x-1/2 bg-[#DDE2E4] rtl:translate-x-1/2"
                style={{ top: dotCentre, bottom: -DOT_CENTRE }}
              />
            )}

            {/* Sits on the rail, pulled half its own width across it, mirrored
                for RTL since translate does not flip on its own. The newest one
                is larger and takes the brand blue, with a wider ring so it
                reads as a marker on the line rather than another bead. */}
            <span
              aria-hidden="true"
              className={cn(
                "absolute start-0 -translate-x-1/2 rounded-full ring-white rtl:translate-x-1/2",
                isLatest
                  ? "size-3.5 bg-[#00A9E7] ring-[5px]"
                  : "size-2.5 bg-[#C7D0D6] ring-4"
              )}
              style={{ top: dotCentre - (isLatest ? 7 : 5) }}
            />

            {/* The newest entry is a tinted card; the rest are plain rows on
                the rail. Everything else on this page is white or #F6F8F9, so
                the tint is the only brand-coloured surface on screen and the
                eye lands on it first. Deliberately no accent bar on its start
                edge — the rail and its dot are already two vertical marks
                there, and a third would read as clutter. */}
            <div
              className={cn(
                isLatest &&
                  "rounded-xl border border-[#B3E2F6] bg-[#F2FAFD] p-3.5"
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                {isLatest && (
                  <span className="text-[11px] font-semibold uppercase leading-4 tracking-[0.08em] text-[#00668F]">
                    {translate("Latest update", "آخر تحديث")}
                  </span>
                )}
                {entry?.createdon && (
                  <p
                    className={cn(
                      "text-xs leading-4 text-[#6E7C87]",
                      isLatest ? "font-normal" : "font-medium"
                    )}
                  >
                    {formatCrmTimestamp(entry.createdon)}
                  </p>
                )}
              </div>

              {/* Capped at a comfortable measure rather than the column width.
                  Even inside the 1200px page the history column is ~790px,
                  which is about 110 characters a line — past the point where
                  the eye starts losing its place on the way back to the left
                  margin. */}
              {message && (
                <p
                  className={cn(
                    "mt-2 max-w-[68ch] whitespace-pre-line leading-6 text-[#1A2024]",
                    isLatest ? "text-[15px] font-medium" : "text-sm"
                  )}
                >
                  {message}
                </p>
              )}
            </div>
          </motion.li>
        );
      })}
    </motion.ol>
  );
};

export default ClaimTimeline;
